import { inject, Injectable, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { of, Subscription } from 'rxjs';
import { catchError, finalize, map, mergeMap, switchMap, take, tap, withLatestFrom } from 'rxjs/operators';
import { AlertService } from '@cde-fe-app/app/cde-toast/services/alert.service';
import { TOASTR_ALERT_OPTIONS } from '@cde-fe-app/app/cde-toast/utils/toastr-alert-options';
import { ToasterClass, ToasterPosition, ToasterType } from '@cde-fe-app/app/cde-toast/utils/toastr-options';
import { Alarm, AlarmsByMonth, AlarmsDaysListRequest } from '@cde-fe-app/app/shared/models/alarm.model';
import { ExtendLicensesGetDealerData, LicenseListRequest } from '@cde-fe-app/app/shared/models/license.model';
import {
  IMachineComponentEditedItem,
  IMachineComponentItem,
  ISoftwareComponent,
} from '@cde-fe-app/app/shared/models/machine.components.model';
import { IMachineConsents } from '@cde-fe-app/app/shared/models/machine.consents.model';
import { IDeleteImageRequest, IMachineImage } from '@cde-fe-app/app/shared/models/machine.image.model';
import { LubricantsAdvisor } from '@cde-fe-app/app/shared/models/machine.lubricants.model';
import { IOperatingInstructions } from '@cde-fe-app/app/shared/models/machine.manuals.model';
import {
  Category,
  Contracts,
  IMachineBasePatch,
  IMachinePatchResponse,
  MachineBase,
} from '@cde-fe-app/app/shared/models/machine.model';
import { IMachinesNote, IMachinesNoteRequest } from '@cde-fe-app/app/shared/models/machine.notes.model';
import { IOnboardClaasMachineError } from '@cde-fe-app/app/shared/models/machine.onboarding.model';
import {
  IEventsResponse,
  IPointDataList,
  ITelemetryEventRequest,
} from '@cde-fe-app/app/shared/models/machine.telemetry.model';
import { IKpiWidgetData, IMachineTelemetryData, IMeasurements } from '@cde-fe-app/app/shared/models/measurements.model';
import { IMachineRetrofitsResponse, IRetrofit } from '@cde-fe-app/app/shared/models/retrofit.model';
import { ServiceCallResponse } from '@cde-fe-app/app/shared/models/service-call.model';
import { AlarmService } from '@cde-fe-app/app/shared/services/alarm.service';
import { DealerService } from '@cde-fe-app/app/shared/services/dealer.service';
import { MachinesService } from '@cde-fe-app/app/shared/services/machines.service';
import { MaintenanceService } from '@cde-fe-app/app/shared/services/maintenance.service';
import { NotesService } from '@cde-fe-app/app/shared/services/notes.service';
import { RetrofitService } from '@cde-fe-app/app/shared/services/retrofit.service';
import { TelemetryService } from '@cde-fe-app/app/shared/services/telemetry.service';
import * as RootState from '@cde-fe-app/app/shared/state';
import { getQueryDivider, resolveShopUrl } from '@cde-fe-app/app/shared/utils/resolve-shop-url';
import * as MachineInfoActions from './machine-details.actions';
import { userSelector } from '../auth';
import { getEquipmentList } from '../machine-overview';
import { getCurrentOrganisation } from '../organisations';
import { shopsStore } from '../shops/shops.store';

@Injectable()
export class MachinesDetailsEffects implements OnDestroy {
  readonly actions$ = inject(Actions);
  readonly alarmService = inject(AlarmService);
  readonly alertService = inject(AlertService);
  readonly machinesService = inject(MachinesService);
  readonly maintenanceService = inject(MaintenanceService);
  readonly notesService = inject(NotesService);
  readonly dealerService = inject(DealerService);
  readonly store = inject<Store<RootState.IState>>(Store<RootState.IState>);
  readonly telemetryService = inject(TelemetryService);
  readonly translate = inject(TranslateService);
  readonly retrofitService = inject(RetrofitService);
  readonly router = inject(Router);
  readonly shopsStore = inject(shopsStore);

  alertOptions = {
    ...TOASTR_ALERT_OPTIONS,
    timeOut: 3000,
  };

  details$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MachineInfoActions.DETAILS),
      map((action: MachineInfoActions.Details) => action.payload),
      withLatestFrom(this.store.select(getEquipmentList)),
      switchMap(([id, equipmentList]) => {
        const machine = (equipmentList ?? []).find((item: MachineBase) => item.id === id) ?? null;

        if (!machine) {
          void this.router.navigate(['not-found']);
          return of(new MachineInfoActions.ClearEntities(), new MachineInfoActions.DetailsComplete());
        }

        return of(new MachineInfoActions.DetailsSuccess(machine), new MachineInfoActions.DetailsComplete());
      }),
      catchError(error => of(new MachineInfoActions.DetailsFailure(error), new MachineInfoActions.DetailsComplete()))
    )
  );

  track$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MachineInfoActions.TRACK),
      map((action: MachineInfoActions.Track) => action.payload),
      switchMap(payload =>
        this.telemetryService.getTrack(payload.id, payload.timeZone, payload.measuredAtLocalDate).pipe(
          map((track: string) => new MachineInfoActions.TrackSuccess(track)),
          catchError(error => of(new MachineInfoActions.TrackFailure(error))),
          finalize(() => {
            this.store.dispatch(new MachineInfoActions.TrackComplete());
          })
        )
      )
    )
  );

  telemetryData$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MachineInfoActions.TELEMETRY_ALARM_DATA),
      map((action: MachineInfoActions.TelemetryDaysData) => action.payload),
      switchMap(payload =>
        this.telemetryService
          .getTelemetryDays(payload.id, payload.timeZone, payload.localMonth, payload.localYear)
          .pipe(
            map((dates: string[]) => new MachineInfoActions.TelemetryDaysDataSuccess(dates)),
            catchError(error => of(new MachineInfoActions.TelemetryDaysDataFailure(error))),
            finalize(() => {
              this.store.dispatch(new MachineInfoActions.TelemetryDaysDataComplete());
            })
          )
      )
    )
  );

  trackCoordinates$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MachineInfoActions.TRACK_COORDINATES),
      map((action: MachineInfoActions.TrackCoordinates) => action.payload),
      switchMap(payload =>
        this.telemetryService
          .getTrackCoordinates(
            payload.id,
            payload.timeZone,
            payload.measuredFromLocalDate,
            payload.measuredUntilLocalDate
          )
          .pipe(
            map(
              (track: string) => new MachineInfoActions.TrackCoordinatesSuccess(track) // TODO
            ),
            catchError(error => of(new MachineInfoActions.TrackCoordinatesFailure(error))),
            finalize(() => {
              this.store.dispatch(new MachineInfoActions.TrackCoordinatesComplete());
            })
          )
      )
    )
  );

  trackAlarm$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MachineInfoActions.TRACK_ALARM_DATE),
      map((action: MachineInfoActions.TrackAlarmDate) => action.payload),
      switchMap(payload =>
        this.alarmService
          .getAlarmsByRange(
            payload.id,
            payload.timeZone,
            payload.measuredFromLocalDate,
            payload.measuredUntilLocalDate,
            payload.language
          )
          .pipe(
            map((alarms: Alarm[]) => new MachineInfoActions.TrackAlarmDateSuccess(alarms)),
            catchError(error => of(new MachineInfoActions.TrackAlarmDateFailure(error))),
            finalize(() => {
              this.store.dispatch(new MachineInfoActions.TrackAlarmDateComplete());
            })
          )
      )
    )
  );

  alarmDays$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MachineInfoActions.ALARMS_DAYS),
      map((action: MachineInfoActions.AlarmsDaysList) => action.payload),
      switchMap((payload: AlarmsDaysListRequest) =>
        this.alarmService.getAlarmsByMonth(payload.id, payload.timeZone, payload.month, payload.year).pipe(
          take(1),
          map((alarmDays: AlarmsByMonth) => new MachineInfoActions.AlarmsDaysSuccess(alarmDays)),
          catchError(error => of(new MachineInfoActions.AlarmsDaysFailure(error))),
          finalize(() => {
            this.store.dispatch(new MachineInfoActions.AlarmsDaysComplete());
          })
        )
      )
    )
  );

  contracts$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MachineInfoActions.CONTRACTS),
      map((action: MachineInfoActions.ContractsList) => action),
      switchMap(payload =>
        this.machinesService.getContracts(payload.uuid, payload.language).pipe(
          take(1),
          map((info: Contracts) => new MachineInfoActions.ContractsSuccess(info)),
          catchError(error => of(new MachineInfoActions.ContractsFailure(error))),
          finalize(() => {
            this.store.dispatch(new MachineInfoActions.ContractsComplete());
          })
        )
      )
    )
  );

  licenses$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MachineInfoActions.LICENSES),
      map((action: MachineInfoActions.LicensesList) => action.payload),
      switchMap((payload: LicenseListRequest) =>
        this.machinesService.getLicenses(payload.equipmentId, payload.language).pipe(
          take(1),
          map(licenses => new MachineInfoActions.LicensesSuccess(licenses)),
          catchError(error => of(new MachineInfoActions.LicensesFailure(error))),
          finalize(() => {
            this.store.dispatch(new MachineInfoActions.LicensesComplete());
          })
        )
      )
    )
  );

  activateLicense$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MachineInfoActions.ACTIVATE_LICENSES),
      map((action: MachineInfoActions.ActivateLicense) => action.payload),
      mergeMap(payload =>
        this.machinesService
          .activateLicense(payload.equipmentId as string, payload.licenseId as string, payload.language as string)
          .pipe(
            take(1),
            map(license => {
              this.alertService.callSnackbarSuccess(
                this.translate.instant('license.farm.license_activation.success'),
                '',
                ToasterType.snackbarSuccess,
                {
                  positionClass: ToasterPosition.bottomCenter,
                }
              );

              return new MachineInfoActions.ActivateLicenseSuccess(license);
            }),
            catchError(error => {
              this.alertService.showError(
                this.translate.instant('machine_details.license.activation_failed'),
                '',
                'alert-error-icon',
                this.alertOptions
              );
              return of(
                new MachineInfoActions.ActivateLicenseFailure({
                  ...error,
                  data: { ...error.data, licenseId: payload.licenseId },
                })
              );
            }),
            finalize(() => {
              this.store.dispatch(new MachineInfoActions.ActivateLicenseComplete());
            })
          )
      )
    )
  );

  extendLicenseGetDealerData$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MachineInfoActions.EXTEND_LICENSES_DEALER_DATA),
      map((action: MachineInfoActions.ExtendLicensesDealerData) => action.payload),
      switchMap((payload: ExtendLicensesGetDealerData) =>
        this.dealerService.getDealer(payload.dealerId, payload.language).pipe(
          map(license => new MachineInfoActions.ExtendLicensesDealerDataSuccess(license)),
          catchError(error => of(new MachineInfoActions.ExtendLicensesDealerDataFailure(error))),
          finalize(() => {
            this.store.dispatch(new MachineInfoActions.ExtendLicensesDealerDataComplete());
          })
        )
      )
    )
  );

  lubricants$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MachineInfoActions.LUBRICANTS),
      map((action: MachineInfoActions.LubricantsList) => action.payload),
      switchMap(payload =>
        this.machinesService.getLubricants(payload).pipe(
          take(1),
          map(lubricants => {
            const lubricantsList: LubricantsAdvisor[] = Object.values(lubricants);
            return new MachineInfoActions.LubricantsSuccess(lubricantsList);
          }),
          catchError(error => of(new MachineInfoActions.LubricantsFailure(error))),
          finalize(() => {
            this.store.dispatch(new MachineInfoActions.LubricantsComplete());
          })
        )
      )
    )
  );

  instructions$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MachineInfoActions.INSTRUCTIONS),
      map((action: MachineInfoActions.InstructionsList) => action.payload),
      switchMap(payload =>
        this.machinesService.getInstructions(payload).pipe(
          take(1),
          map(instructions => {
            const instructionsList: IOperatingInstructions[] = Object.values(instructions);
            return new MachineInfoActions.InstructionsSuccess(instructionsList);
          }),
          catchError(error => of(new MachineInfoActions.InstructionsFailure(error))),
          finalize(() => {
            this.store.dispatch(new MachineInfoActions.InstructionsComplete());
          })
        )
      )
    )
  );

  eventsTelemetry$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MachineInfoActions.EVENT_TELEMETRY),
      withLatestFrom(this.store),
      map(
        ([action, store]: [MachineInfoActions.TelemetryEvents, RootState.IState]) =>
          [action.payload, store.machineDetails.eventTelemetryPending] as [ITelemetryEventRequest, boolean]
      ),
      mergeMap(([payload, pending]) =>
        this.telemetryService
          .getTelemetryEvent(
            payload.id,
            payload.eventName,
            payload.timeZone,
            payload.measuredFromLocalDate,
            payload.measuredUntilLocalDate,
            pending
          )
          .pipe(
            map(
              data =>
                new MachineInfoActions.TelemetryEventsSuccess({
                  data: data as IEventsResponse,
                  eventName: payload.eventName,
                })
            ),
            catchError(error => of(new MachineInfoActions.TelemetryEventsFailure(error))),
            finalize(() => {
              this.store.dispatch(new MachineInfoActions.TelemetryEventsComplete());
            })
          )
      )
    )
  );

  eventNames$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MachineInfoActions.EVENT_NAME),
      map((action: MachineInfoActions.EventName) => action.payload),
      switchMap((payload: string) =>
        this.machinesService.getEventNames(payload).pipe(
          map((names: string[]) => new MachineInfoActions.EventNameSuccess(names)),
          catchError(error => of(new MachineInfoActions.EventNameFailure(error))),
          finalize(() => {
            this.store.dispatch(new MachineInfoActions.EventNameComplete());
          })
        )
      )
    )
  );

  telemetryPointdata$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MachineInfoActions.TELEMETRY_POINTDATA),
      map((action: MachineInfoActions.TelemetryPointdata) => action.payload),
      mergeMap(payload => {
        const fallbackDate =
          payload.measuredLocalDates && payload.measuredLocalDates.length ? payload.measuredLocalDates[0] : '';
        return this.machinesService
          .getPointData(
            payload.id,
            payload.measurementName,
            payload.timeZone,
            payload.measuredFromLocalDate || fallbackDate,
            payload.measuredUntilLocalDate || fallbackDate,
            payload.measuredLocalDates || [],
            payload.measurementType
          )
          .pipe(
            map((pointdataList: IPointDataList) => {
              const ret: IPointDataList = {
                content: pointdataList.content,
                legend: {
                  items: pointdataList.legend.items,
                  name: this.translate.instant('filter.pointData.' + pointdataList.metadata.key),
                },
                metadata: pointdataList.metadata,
              };

              return new MachineInfoActions.TelemetryPointdataSuccess({
                pointDataList: ret,
              });
            }),
            catchError(error => of(new MachineInfoActions.TelemetryPointdataFailure(error))),
            finalize(() => {
              this.store.dispatch(new MachineInfoActions.TelemetryPointdataComplete());
            })
          );
      })
    )
  );

  telemetryPointdataDays$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(MachineInfoActions.TELEMETRY_POINTDATA_DAYS),
      map((action: MachineInfoActions.TelemetryPointdataDays) => action.payload),
      mergeMap(payload =>
        this.machinesService
          .getPointDataDays(
            payload.id,
            payload.measurementName,
            payload.timeZone,
            payload.measuredFromLocalDate,
            payload.measuredUntilLocalDate,
            payload.measurementType
          )
          .pipe(
            map((pointDataDays: IPointDataList) => {
              return new MachineInfoActions.TelemetryPointdataDaysSuccess({
                pointDataDays: pointDataDays,
              });
            }),
            catchError(error => of(new MachineInfoActions.TelemetryPointdataDaysFailure(error))),
            finalize(() => {
              this.store.dispatch(new MachineInfoActions.TelemetryPointdataDaysComplete());
            })
          )
      )
    );
  });

  imageFileAdd$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MachineInfoActions.IMAGEADD),
      mergeMap((data: { payload: IMachineImage }) =>
        this.machinesService.postImage(data.payload).pipe(
          take(1),
          map((imageInfo: IMachineImage) => new MachineInfoActions.ImageAddSuccess(imageInfo)),
          catchError(error => of(new MachineInfoActions.ImageAddFailure(error))),
          finalize(() => {
            this.store.dispatch(new MachineInfoActions.ImageAddComplete());
          })
        )
      )
    )
  );

  imageFileDelete$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MachineInfoActions.IMAGEDELETE),
      map((action: MachineInfoActions.ImageDelete) => action.payload),
      switchMap((payload: IDeleteImageRequest) =>
        this.machinesService.deleteImage(payload).pipe(
          map((imageInfo: string | undefined) => {
            this.alertService.callSnackbarSuccess(
              this.translate.instant('machine_details.edit.image_delete_success'),
              '',
              ToasterType.snackbarSuccess,
              {}
            );
            return new MachineInfoActions.ImageDeleteSuccess(imageInfo);
          }),
          catchError(error => {
            this.alertService.showError(
              this.translate.instant('machine_details.edit.could_not_remove_image'),
              this.translate.instant('alert_message.try_again'),
              'alert-error-icon',
              this.alertOptions
            );
            return of(new MachineInfoActions.ImageAddFailure(error));
          }),
          finalize(() => {
            this.store.dispatch(new MachineInfoActions.ImageDeleteComplete());
          })
        )
      )
    )
  );

  machinePatch$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MachineInfoActions.MACHINEPATCH),
      map((action: MachineInfoActions.MachinePatch) => action.payload),
      switchMap((payload: IMachineBasePatch) =>
        this.machinesService.patchMachine(payload).pipe(
          map((response: IMachinePatchResponse) => new MachineInfoActions.MachinePatchSuccess(response)),
          catchError(error => {
            this.alertService.showError(
              this.translate.instant('machine_details.edit.error'),
              this.translate.instant('alert_message.try_again'),
              'alert-error-icon',
              this.alertOptions
            );
            return of(new MachineInfoActions.MachinePatchFailure(error));
          }),
          finalize(() => {
            this.store.dispatch(new MachineInfoActions.MachinePatchComplete());
          })
        )
      )
    )
  );

  pointDataConfiguredSignals$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MachineInfoActions.POINTDATA_CONFIGURED_SIGNALS),
      map((action: MachineInfoActions.PointDataConfiguredSignals) => action.payload),
      switchMap(payload =>
        this.machinesService.getPointDataConfiguredSignals(payload.id, payload.timeZone).pipe(
          take(1),
          map(
            pointDataConfiguredSignals =>
              new MachineInfoActions.PointDataConfiguredSignalsSuccess(pointDataConfiguredSignals)
          ),
          catchError(error => of(new MachineInfoActions.PointDataConfiguredSignalsFailure(error))),
          finalize(() => {
            this.store.dispatch(new MachineInfoActions.PointDataConfiguredSignalsComplete());
          })
        )
      )
    )
  );

  notes$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MachineInfoActions.NOTES),
      map((action: MachineInfoActions.NotesList) => action.payload),
      switchMap((payload: { type: string; machineId: string; sortBy: string }) =>
        this.notesService.getAllNotes(payload.type, payload.machineId, payload.sortBy).pipe(
          take(1),
          map((info: IMachinesNote) => {
            const notesList: IMachinesNote[] = Object.values(info);
            return new MachineInfoActions.NotesSuccess(notesList);
          }),
          catchError(error => of(new MachineInfoActions.NotesFailure(error))),
          finalize(() => {
            this.store.dispatch(new MachineInfoActions.NotesComplete());
          })
        )
      )
    )
  );

  addNote$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(MachineInfoActions.ADD_NOTE),
        map((action: MachineInfoActions.AddNote) => action.payload),
        switchMap((payload: IMachinesNoteRequest) =>
          this.notesService.addNewNote(payload).pipe(
            take(1),
            map((result: IMachinesNote) => {
              if (result) {
                const newNote = { ...result };
                this.store.dispatch(new MachineInfoActions.AddNoteSuccess(newNote));
                this.alertService.showSuccess(
                  this.translate.instant('notes.note_add_success'),
                  '',
                  'snackbar-success',
                  {
                    positionClass: ToasterPosition.bottomLeft,
                    type: ToasterType.snackbarSuccess,
                    toastClass: ToasterClass.snackbarSuccess,
                    closeButton: true,
                  }
                );
              }
            }),
            catchError(error => {
              this.alertService.showError(
                this.translate.instant('notes.note_add_failure'),
                this.translate.instant('alert_message.try_again'),
                'alert-error-icon',
                this.alertOptions
              );
              return of(new MachineInfoActions.AddNoteFailure(error));
            }),
            finalize(() => {
              this.store.dispatch(new MachineInfoActions.AddNoteComplete());
            })
          )
        )
      ),
    { dispatch: false }
  );

  updateNote$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MachineInfoActions.UPDATE_NOTE),
      map((action: MachineInfoActions.UpdateNote) => action.payload),
      switchMap((payload: { data: IMachinesNoteRequest; noteId: string }) =>
        this.notesService.updateNoteById(payload.data, payload.noteId).pipe(
          take(1),
          map((note: IMachinesNote) => {
            this.alertService.callSnackbarSuccess(
              this.translate.instant('notes.note_update_success'),
              '',
              ToasterType.snackbarSuccess,
              {}
            );
            return new MachineInfoActions.UpdateNoteSuccess(note);
          }),
          catchError(error => {
            this.alertService.showError(
              this.translate.instant('configuration.note_update_failure'),
              this.translate.instant('alert_message.try_again'),
              'alert-error-icon',
              this.alertOptions
            );

            return of(new MachineInfoActions.UpdateNoteFailure(error));
          }),
          finalize(() => {
            this.store.dispatch(new MachineInfoActions.UpdateNoteComplete());
          })
        )
      )
    )
  );

  deleteNote$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MachineInfoActions.DELETE_NOTE),
      map((action: MachineInfoActions.DeleteNote) => action.payload),
      switchMap((payload: { noteId: string }) =>
        this.notesService.deleteNoteById(payload.noteId).pipe(
          take(1),
          map(() => {
            this.alertService.callSnackbarSuccess(
              this.translate.instant('notes.note_delete_success'),
              '',
              ToasterType.snackbarSuccess,
              {}
            );
            return new MachineInfoActions.DeleteNoteSuccess({
              noteId: payload.noteId,
            });
          }),
          catchError(error => {
            this.alertService.showError(
              this.translate.instant('notes.note_delete_failure'),
              this.translate.instant('alert_message.try_again'),
              'alert-error-icon',
              this.alertOptions
            );
            return of(new MachineInfoActions.DeleteNoteFailure(error));
          }),
          finalize(() => {
            this.store.dispatch(new MachineInfoActions.DeleteNoteComplete());
          })
        )
      )
    )
  );

  components$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MachineInfoActions.COMPONENTS),
      map((action: MachineInfoActions.ComponentsList) => action.payload),
      switchMap((payload: { machineId: string; language: string }) =>
        this.machinesService.getSoftwareComponents(payload.machineId, payload.language).pipe(
          take(1),
          map((info: ISoftwareComponent) => {
            const componentsEdited: IMachineComponentEditedItem[] = info.softwareComponents.map(
              (item: IMachineComponentItem) => ({
                components: item.componentText,
                lastUpdate: item.backupAtLocalDate,
                version: item.version,
              })
            );

            return new MachineInfoActions.ComponentsSuccess(componentsEdited);
          }),
          catchError(error => of(new MachineInfoActions.ComponentsFailure(error))),
          finalize(() => {
            this.store.dispatch(new MachineInfoActions.ComponentsComplete());
          })
        )
      )
    )
  );

  getMachineUISections$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MachineInfoActions.GET_MACHINE_UI_SECTIONS),
      map((action: MachineInfoActions.GetMachineUISections) => action.machineId),
      switchMap((machineId: string) =>
        this.machinesService.getMachineUISections(machineId).pipe(
          map(res => new MachineInfoActions.GetMachineUISectionsSuccess(res)),
          catchError(error => of(new MachineInfoActions.GetMachineUISectionsFailure(error))),
          finalize(() => this.store.dispatch(new MachineInfoActions.GetMachineUISectionsComplete()))
        )
      )
    )
  );

  machineTelemetryData$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MachineInfoActions.MACHINE_TELEMETRY_DATA),
      switchMap((payload: { uuid: string; timeZone: string; category?: Category }) => {
        return this.machinesService.getEquipmentTelemetryData(payload.uuid, payload.timeZone).pipe(
          take(1),
          map((res: IMachineTelemetryData) => new MachineInfoActions.MachineTelemetryDataSuccess(res)),
          catchError(error => of(new MachineInfoActions.MachineTelemetryDataFailure(error))),
          finalize(() => this.store.dispatch(new MachineInfoActions.MachineTelemetryDataComplete()))
        );
      })
    )
  );

  machineMapPopupTelemetryData$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MachineInfoActions.MACHINE_MAP_POPUP_TELEMETRY_DATA),
      switchMap((payload: { uuid: string; timeZone: string }) =>
        this.machinesService.getEquipmentMapPopupTelemetryData(payload.uuid, payload.timeZone).pipe(
          take(1),
          map((res: IMeasurements[]) => new MachineInfoActions.MachineMapPopupTelemetryDataSuccess(res)),
          catchError(error => of(new MachineInfoActions.MachineMapPopupTelemetryDataFailure(error))),
          finalize(() => this.store.dispatch(new MachineInfoActions.MachineMapPopupTelemetryDataComplete()))
        )
      )
    )
  );

  machineKpiWidgetyData$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MachineInfoActions.MACHINE_KPI_WIDGET_DATA),
      mergeMap((payload: { uuid: string; timeZone: string }) =>
        this.machinesService.getEquipmentKpiWigetData(payload.uuid, payload.timeZone).pipe(
          map((res: IKpiWidgetData) => new MachineInfoActions.MachineKpiWidgetDataSuccess(res)),
          catchError(error => of(new MachineInfoActions.MachineKpiWidgetDataFailure(error))),
          finalize(() => this.store.dispatch(new MachineInfoActions.MachineKpiWidgetDataComplete()))
        )
      )
    )
  );

  getMachineRetrofits$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MachineInfoActions.GET_MACHINE_RETROFITS),
      mergeMap((action: MachineInfoActions.GetMachineRetrofits) =>
        this.retrofitService.getRetrofits(action.machineId, action.language, action.timeZone).pipe(
          map(
            (response: IMachineRetrofitsResponse) =>
              new MachineInfoActions.GetMachineRetrofitsSuccess(action.machineId, response)
          ),
          catchError(error => {
            if (error.failCode !== 0 && error.failCode < 500) {
              this.alertService.showError(
                this.translate.instant('configuration.failed_to_load_config'),
                this.translate.instant('alert_message.network_error'),
                'alert-error-icon',
                this.alertOptions
              );
            }
            return of(new MachineInfoActions.GetMachineRetrofitsFailure(error));
          }),
          finalize(() => this.store.dispatch(new MachineInfoActions.GetMachineRetrofitsComplete()))
        )
      )
    )
  );

  createMachineRetrofit$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MachineInfoActions.CREATE_MACHINE_RETROFIT),
      mergeMap((action: MachineInfoActions.CreateMachineRetrofit) =>
        this.retrofitService
          .createRetrofit(action.machineId, action.name, action.code, action.language, action.timeZone)
          .pipe(
            map(
              (response: IRetrofit) => new MachineInfoActions.CreateMachineRetrofitSuccess(action.machineId, response)
            ),
            catchError(error => {
              this.alertService.showError(
                this.translate.instant('configuration.could_not_add_config'),
                this.translate.instant('alert_message.try_again'),
                'alert-error-icon',
                this.alertOptions
              );

              return of(new MachineInfoActions.CreateMachineRetrofitFailure(error));
            }),
            finalize(() => {
              this.store.dispatch(new MachineInfoActions.CreateMachineRetrofitComplete());
            })
          )
      )
    )
  );

  deleteMachineRetrofit$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MachineInfoActions.DELETE_MACHINE_RETROFIT),
      mergeMap((action: MachineInfoActions.DeleteMachineRetrofit) =>
        this.retrofitService.deleteRetrofit(action.machineId, action.machineRetrofitId).pipe(
          map(() => new MachineInfoActions.DeleteMachineRetrofitSuccess(action.machineId, action.machineRetrofitId)),
          catchError(error => {
            this.alertService.showError(
              this.translate.instant('configuration.could_not_delete_config'),
              this.translate.instant('alert_message.try_again'),
              'alert-error-icon',
              this.alertOptions
            );

            return of(new MachineInfoActions.DeleteMachineRetrofitFailure(error));
          }),
          finalize(() => {
            this.store.dispatch(new MachineInfoActions.DeleteMachineRetrofitComplete());
          })
        )
      )
    )
  );

  patchMachineRetrofit$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MachineInfoActions.PATCH_MACHINE_RETROFIT),
      mergeMap((action: MachineInfoActions.PatchMachineRetrofit) =>
        this.retrofitService
          .patchRetrofit(action.machineId, action.machineRetrofitId, action.language, action.timeZone, action.request)
          .pipe(
            map(
              response =>
                new MachineInfoActions.PatchMachineRetrofitSuccess(action.machineId, action.machineRetrofitId, response)
            ),
            catchError(error => {
              this.alertService.showError(
                this.translate.instant('configuration.could_not_update_config'),
                this.translate.instant('alert_message.try_again'),
                'alert-error-icon',
                this.alertOptions
              );

              return of(new MachineInfoActions.PatchMachineRetrofitFailure(error));
            }),
            finalize(() => {
              this.store.dispatch(new MachineInfoActions.PatchMachineRetrofitComplete());
            })
          )
      )
    )
  );

  getMachineConsents$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MachineInfoActions.GET_MACHINE_CONSENTS),
      mergeMap((action: MachineInfoActions.GetMachineConsents) =>
        this.machinesService.getMachineConsents(action.machineId).pipe(
          map((response: IMachineConsents) => new MachineInfoActions.GetMachineConsentsSuccess(response)),
          catchError(error => of(new MachineInfoActions.GetMachineConsentsFailure(error))),
          finalize(() => this.store.dispatch(new MachineInfoActions.GetMachineConsentsComplete()))
        )
      )
    )
  );

  patchMachineConsents$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MachineInfoActions.PATCH_MACHINE_CONSENTS),
      mergeMap((action: MachineInfoActions.PatchMachineConsents) =>
        this.machinesService.patchMachineConsents(action.machineId, action.request).pipe(
          map((response: IMachineConsents) => new MachineInfoActions.PatchMachineConsentsSuccess(response)),
          catchError(error => of(new MachineInfoActions.PatchMachineConsentsFailure(error))),
          finalize(() => this.store.dispatch(new MachineInfoActions.PatchMachineConsentsComplete()))
        )
      )
    )
  );

  handlePatchMachineConsentsSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(MachineInfoActions.PATCH_MACHINE_CONSENTS_SUCCESS),
        tap((action: MachineInfoActions.PatchMachineConsentsSuccess) =>
          this.router.navigate(['machines', 'details', action.machineConsents.id])
        ),
        tap((_action: MachineInfoActions.PatchMachineConsentsSuccess) => {
          this.alertService.callSnackbarSuccess(
            this.translate.instant('machine_details.maco.update_success'),
            '',
            ToasterType.snackbarSuccess,
            {
              positionClass: ToasterPosition.bottomCenter,
            }
          );
        })
      ),
    { dispatch: false }
  );

  showPatchMachineConsentsFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(MachineInfoActions.PATCH_MACHINE_CONSENTS_FAILURE),
        tap((action: MachineInfoActions.PatchMachineConsentsFailure) => {
          const error = action.payload as ServiceCallResponse;
          const errorData = error.data as IOnboardClaasMachineError;
          if (errorData?.status === 409) {
            this.alertService.showErrorInNewAlertBanner(
              this.translate.instant(errorData.localMessageKey),
              '',
              'alert-error-icon',
              this.alertOptions
            );
          } else {
            this.alertService.showError(
              this.translate.instant('machine_details.maco.update_error'),
              this.translate.instant('alert_message.try_again'),
              'alert-error-icon',
              this.alertOptions
            );
          }
        })
      ),
    { dispatch: false }
  );

  addToPartsShopShoppingCart$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MachineInfoActions.ADD_TO_PARTS_SHOP_SHOPPING_CART),
      mergeMap((action: MachineInfoActions.AddToPartsShopShoppingCart) =>
        this.machinesService.addToPartsShopShoppingCart(action.request).pipe(
          map(() => new MachineInfoActions.AddToPartsShopShoppingCartSuccess(action.request, action.machineSerial)),
          catchError(error => of(new MachineInfoActions.AddToPartsShopShoppingCartFailure(error))),
          finalize(() => this.store.dispatch(new MachineInfoActions.AddToPartsShopShoppingCartComplete()))
        )
      )
    )
  );

  addToPartsShopShoppingCartSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(MachineInfoActions.ADD_TO_PARTS_SHOP_SHOPPING_CART_SUCCESS),
        tap((action: MachineInfoActions.AddToPartsShopShoppingCart) => {
          this.alertService.showSuccess(
            this.translate.instant('machine_details.parts-shop-cart.add-to-cart-success'),
            '',
            'snackbar-success',
            {
              positionClass: ToasterPosition.bottomLeft,
              type: ToasterType.snackbarSuccess,
              toastClass: ToasterClass.snackbarSuccess,
              closeButton: true,
            }
          );

          this.openPartsShop(action.request.shopId);
        })
      ),
    { dispatch: false }
  );

  addToPartsShopShoppingCartFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(MachineInfoActions.ADD_TO_PARTS_SHOP_SHOPPING_CART_FAILURE),
        tap((_action: MachineInfoActions.AddToPartsShopShoppingCart) => {
          this.alertService.showError(
            this.translate.instant('machine_details.parts-shop-cart.add-to-cart-error'),
            '',
            'alert-error-icon',
            this.alertOptions
          );
        })
      ),
    { dispatch: false }
  );

  createDealerChange$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MachineInfoActions.CREATE_DEALER_CHANGE),
      mergeMap((action: MachineInfoActions.CreateDealerChange) =>
        this.machinesService.createDealerChange(action.request).pipe(
          map(response => new MachineInfoActions.CreateDealerChangeSuccess(response)),
          catchError(error => of(new MachineInfoActions.CreateDealerChangeFailure(error, action.request.id))),
          finalize(() => this.store.dispatch(new MachineInfoActions.CreateDealerChangeComplete()))
        )
      )
    )
  );

  getDealerChange$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MachineInfoActions.GET_DEALER_CHANGE),
      mergeMap((action: MachineInfoActions.GetDealerChange) =>
        this.machinesService.getDealerChange(action.machineId).pipe(
          map(response => new MachineInfoActions.GetDealerChangeSuccess(response)),
          catchError(error => of(new MachineInfoActions.GetDealerChangeFailure(error, action.machineId))),
          finalize(() => this.store.dispatch(new MachineInfoActions.GetDealerChangeComplete()))
        )
      )
    )
  );

  private currentOrganisationId: string | null = null;
  private userLanguage: string | null = null;
  private subscription: Subscription = new Subscription();

  constructor() {
    this.subscription.add(
      this.store.select(getCurrentOrganisation).subscribe(currentOrganisation => {
        this.currentOrganisationId = currentOrganisation?.auth0Id ?? null;
      })
    );

    this.subscription.add(
      this.store.select(userSelector).subscribe(user => {
        this.userLanguage = user?.user_metadata.language ?? null;
      })
    );
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  private openPartsShop(partsShopId: string) {
    const registeredShops = this.shopsStore.shops();
    if (registeredShops == null) {
      return;
    }

    const partsShop = registeredShops.find(s => s.shopId === partsShopId);
    if (partsShop == null) {
      return;
    }

    let url = resolveShopUrl(partsShop, this.currentOrganisationId, null, this.userLanguage ?? 'en-gb');
    const queryDivider = getQueryDivider(url);
    url = `${url}${queryDivider}redirectTo=basket`;

    window.open(url, '_blank');
  }
}
